import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@mlm/ui';
import {
  createCourseTest,
  createTestQuestion,
  deleteCourseTest,
  deleteTestQuestion,
  listCourseTests,
  updateCourseTest,
  updateTestQuestion,
  type CourseDetail,
  type CourseTest,
  type TestQuestion,
  type TestQuestionUpsert,
} from '../lib/api';

/**
 * Tests tab — the quiz gate in front of the certificate. One quiz per
 * chapter plus one final test; a learner must pass every ACTIVE test (that
 * has questions) before the certificate is issued. Multiple-choice grades
 * itself; typed-answer (essay) questions land in the Grading inbox.
 */
export function CourseTestsTab({ course }: { course: CourseDetail }) {
  const qc = useQueryClient();
  const { data: tests = [], isLoading } = useQuery({
    queryKey: ['creator-course-tests', course.id],
    queryFn: () => listCourseTests(course.id),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['creator-course-tests', course.id] });

  const [practice, setPractice] = useState(false);
  const [placement, setPlacement] = useState('');
  const [title, setTitle] = useState('');

  // Slots are counted PER KIND: the unique indexes only bind graded tests, so a
  // practice test may sit beside the graded quiz of the same chapter.
  const sameKind = tests.filter((t) => t.isPractice === practice);
  const usedChapterIds = new Set(sameKind.map((t) => t.chapterId).filter(Boolean));
  const hasFinal = sameKind.some((t) => t.chapterId === null);
  const openSlots: { value: string; label: string }[] = [
    ...(hasFinal
      ? []
      : [
          {
            value: 'final',
            label: practice ? 'Practice for the whole course' : 'Final test (whole course)',
          },
        ]),
    ...course.chapters
      .filter((c) => !usedChapterIds.has(c.id))
      .map((c) => ({
        value: c.id,
        label: practice ? `Practice after: ${c.title}` : `Quiz after: ${c.title}`,
      })),
  ];

  const create = useMutation({
    meta: { successMessage: 'Test created — add questions.' },
    mutationFn: () =>
      createCourseTest(course.id, {
        title: title.trim(),
        chapterId: placement === 'final' ? null : placement,
        isPractice: practice,
      }),
    onSuccess: () => {
      setTitle('');
      setPlacement('');
      invalidate();
    },
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tests</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Learners must pass every active test below — on top of finishing the lessons — before
            their certificate is issued. Multiple-choice questions grade instantly; typed answers
            wait for you in{' '}
            <Link to="/grading" className="font-medium text-primary hover:underline">
              Grading
            </Link>
            . A <strong>practice test</strong> is different: multiple-choice only, retake it as
            often as you like, the answers and explanations show after every attempt, and it never
            stands between a learner and their certificate.
          </p>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={practice}
              onCheckedChange={(v) => {
                setPractice(v === true);
                // The open slots differ per kind, so a carried-over pick could
                // point at a placement this kind cannot use.
                setPlacement('');
              }}
            />
            Practice test — multiple choice only, self-check, never required
          </label>
          {openSlots.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Every chapter and the whole course already have{' '}
              {practice ? 'a practice test' : 'a graded test'}.
            </p>
          )}
          {openSlots.length > 0 && (
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (title.trim() && placement) create.mutate();
              }}
            >
              <div className="space-y-1">
                <label className="text-xs font-medium">Placement</label>
                <Select value={placement || undefined} onValueChange={setPlacement}>
                  <SelectTrigger className="w-full sm:w-64">
                    <SelectValue placeholder="Where does this test sit?" />
                  </SelectTrigger>
                  <SelectContent>
                    {openSlots.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 space-y-1">
                <label className="text-xs font-medium">Title</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Final exam"
                />
              </div>
              <Button type="submit" disabled={!title.trim() || !placement || create.isPending}>
                + Add test
              </Button>
            </form>
          )}
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : tests.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No tests yet — without one, the certificate is earned by finishing the lessons.
            </p>
          ) : null}
        </CardContent>
      </Card>
      {tests.map((t) => (
        <TestCard
          key={t.id}
          test={t}
          chapterTitle={
            t.chapterId
              ? (course.chapters.find((c) => c.id === t.chapterId)?.title ?? 'Removed chapter')
              : null
          }
          onChange={invalidate}
        />
      ))}
    </div>
  );
}

function TestCard({
  test,
  chapterTitle,
  onChange,
}: {
  test: CourseTest;
  chapterTitle: string | null;
  onChange: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [passing, setPassing] = useState(String(test.passingScorePct));
  const [maxAttempts, setMaxAttempts] = useState(test.maxAttempts ? String(test.maxAttempts) : '');
  const update = useMutation({
    meta: { successMessage: 'Test updated.' },
    mutationFn: (patch: Parameters<typeof updateCourseTest>[1]) => updateCourseTest(test.id, patch),
    onSuccess: onChange,
  });
  const remove = useMutation({
    meta: { successMessage: 'Test deleted.' },
    mutationFn: () => deleteCourseTest(test.id),
    onSuccess: onChange,
  });
  const addQuestion = useMutation({
    mutationFn: (input: TestQuestionUpsert) =>
      createTestQuestion(test.id, { ...input, sortOrder: test.questions.length }),
    onSuccess: onChange,
  });

  return (
    <Card className={test.active ? '' : 'opacity-70'}>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">
            {test.title}
            {test.isPractice && (
              <Badge variant="outline" className="ml-2">
                Practice
              </Badge>
            )}
            {!test.active && (
              <Badge variant="secondary" className="ml-2">
                Inactive
              </Badge>
            )}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {chapterTitle
              ? `${test.isPractice ? 'Practice' : 'Quiz'} after “${chapterTitle}”`
              : test.isPractice
                ? 'Practice for the whole course'
                : 'Final test'}{' '}
            · {test.questions.length} question{test.questions.length === 1 ? '' : 's'}
            {test.questions.length === 0 &&
              (test.isPractice ? ' — add one to show it' : ' — not counted until it has one')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => update.mutate({ active: !test.active })}
          >
            {test.active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium">Passing score (%)</label>
            <Input
              className="w-full sm:w-24"
              inputMode="numeric"
              value={passing}
              onChange={(e) => setPassing(e.target.value.replace(/\D/g, ''))}
              onBlur={() => {
                const v = Math.min(100, Math.max(1, Number(passing) || test.passingScorePct));
                setPassing(String(v));
                if (v !== test.passingScorePct) update.mutate({ passingScorePct: v });
              }}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Attempt limit</label>
            {test.isPractice ? (
              <p className="text-xs text-muted-foreground">Unlimited retakes</p>
            ) : (
              <Input
                className="w-full sm:w-24"
                inputMode="numeric"
                placeholder="∞"
                value={maxAttempts}
                onChange={(e) => setMaxAttempts(e.target.value.replace(/\D/g, ''))}
                onBlur={() => {
                  const v = maxAttempts === '' ? null : Math.max(1, Number(maxAttempts));
                  if (v !== test.maxAttempts) update.mutate({ maxAttempts: v });
                }}
              />
            )}
          </div>
        </div>
        <ul className="space-y-2">
          {test.questions.map((q, i) => (
            <QuestionRow
              key={q.id}
              question={q}
              index={i}
              isPractice={test.isPractice}
              onChange={onChange}
            />
          ))}
        </ul>
        <NewQuestionForm
          pending={addQuestion.isPending}
          isPractice={test.isPractice}
          onAdd={(input) => addQuestion.mutate(input)}
        />
      </CardContent>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this test?"
        description="Learners' past attempts are removed with it. Courses without tests fall back to lessons-only certificates."
        confirmLabel="Delete test"
        onConfirm={() => remove.mutate()}
      />
    </Card>
  );
}

function QuestionRow({
  question,
  index,
  isPractice,
  onChange,
}: {
  question: TestQuestion;
  index: number;
  isPractice: boolean;
  onChange: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const remove = useMutation({
    mutationFn: () => deleteTestQuestion(question.id),
    onSuccess: onChange,
  });
  const update = useMutation({
    meta: { successMessage: 'Question updated.' },
    mutationFn: (input: TestQuestionUpsert) => updateTestQuestion(question.id, input),
    onSuccess: () => {
      setEditing(false);
      onChange();
    },
  });

  if (editing) {
    return (
      <li className="rounded border p-3">
        <QuestionForm
          initial={question}
          pending={update.isPending}
          isPractice={isPractice}
          submitLabel="Save"
          onCancel={() => setEditing(false)}
          onSubmit={(input) => update.mutate(input)}
        />
      </li>
    );
  }
  return (
    <li className="flex items-start gap-3 rounded border p-3">
      <span className="mt-0.5 text-xs font-semibold text-muted-foreground">{index + 1}.</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm">{question.prompt}</p>
        {question.kind === 'multiple_choice' ? (
          <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            {question.choices.map((c, i) => (
              <li key={i} className={i === question.correctIndex ? 'font-medium text-primary' : ''}>
                {i === question.correctIndex ? '✓ ' : '· '}
                {c}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">Typed answer — graded by you</p>
        )}
        {question.explanation && (
          <p className="mt-1 text-xs italic text-muted-foreground">
            Explanation: {question.explanation}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {question.points} pt{question.points === 1 ? '' : 's'}
        </span>
        <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
          Edit
        </Button>
        <Button size="sm" variant="ghost" onClick={() => remove.mutate()}>
          Remove
        </Button>
      </div>
    </li>
  );
}

function NewQuestionForm({
  pending,
  isPractice,
  onAdd,
}: {
  pending: boolean;
  isPractice: boolean;
  onAdd: (input: TestQuestionUpsert) => void;
}) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        + Add question
      </Button>
    );
  }
  return (
    <div className="rounded border p-3">
      <QuestionForm
        pending={pending}
        isPractice={isPractice}
        submitLabel="Add question"
        onCancel={() => setOpen(false)}
        onSubmit={(input) => {
          onAdd(input);
          setOpen(false);
        }}
      />
    </div>
  );
}

function QuestionForm({
  initial,
  pending,
  isPractice,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: TestQuestion;
  pending: boolean;
  isPractice: boolean;
  submitLabel: string;
  onSubmit: (input: TestQuestionUpsert) => void;
  onCancel: () => void;
}) {
  // A practice test is multiple-choice only — a typed answer would park the
  // attempt in the grading queue, which is exactly what a self-check must not
  // do. The server refuses it too (400 practice_no_essay).
  const [kind, setKind] = useState<'multiple_choice' | 'essay'>(
    isPractice ? 'multiple_choice' : (initial?.kind ?? 'multiple_choice'),
  );
  const [explanation, setExplanation] = useState(initial?.explanation ?? '');
  const [prompt, setPrompt] = useState(initial?.prompt ?? '');
  const [choices, setChoices] = useState<string[]>(
    initial?.choices.length ? initial.choices : ['', ''],
  );
  const [correctIndex, setCorrectIndex] = useState(initial?.correctIndex ?? 0);
  const [points, setPoints] = useState(String(initial?.points ?? 1));

  const validChoices = choices.map((c) => c.trim()).filter(Boolean);
  const canSubmit =
    prompt.trim().length > 0 &&
    (kind === 'essay' || (validChoices.length >= 2 && correctIndex < validChoices.length));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium">Type</label>
          {isPractice ? (
            // A Select must never hold a value with no matching item, so the
            // practice case is a plain line rather than a one-option picker.
            <p className="text-sm">Multiple choice — auto-graded</p>
          ) : (
            <Select value={kind} onValueChange={(v) => setKind(v as typeof kind)}>
              <SelectTrigger className="w-full sm:w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="multiple_choice">Multiple choice — auto-graded</SelectItem>
                <SelectItem value="essay">Typed answer — you grade it</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium">Points</label>
          <Input
            className="w-20"
            inputMode="numeric"
            value={points}
            onChange={(e) => setPoints(e.target.value.replace(/\D/g, ''))}
          />
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-xs font-medium">Question</label>
        <Textarea
          rows={2}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={
            kind === 'essay' ? 'e.g. Describe your first sales plan.' : 'e.g. What does SRP mean?'
          }
        />
      </div>
      {kind === 'multiple_choice' && (
        <div className="space-y-2">
          <label className="text-xs font-medium">Choices — tick the correct one</label>
          {choices.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <Checkbox
                checked={correctIndex === i}
                onCheckedChange={() => setCorrectIndex(i)}
                aria-label={`Choice ${i + 1} is correct`}
              />
              <Input
                value={c}
                onChange={(e) =>
                  setChoices((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))
                }
                placeholder={`Choice ${i + 1}`}
              />
              {choices.length > 2 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setChoices((prev) => prev.filter((_, j) => j !== i));
                    if (correctIndex === i) setCorrectIndex(0);
                    else if (correctIndex > i) setCorrectIndex(correctIndex - 1);
                  }}
                >
                  ✕
                </Button>
              )}
            </div>
          ))}
          {choices.length < 10 && (
            <Button size="sm" variant="ghost" onClick={() => setChoices((p) => [...p, ''])}>
              + Choice
            </Button>
          )}
        </div>
      )}
      {isPractice && (
        <div className="space-y-1">
          <label className="text-xs font-medium">Explanation</label>
          <Textarea
            rows={2}
            maxLength={2000}
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            placeholder="Why is that the answer? Shown to the learner after they submit."
          />
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!canSubmit || pending}
          onClick={() =>
            onSubmit({
              kind,
              prompt: prompt.trim(),
              points: Math.max(1, Number(points) || 1),
              // Only a practice test ever shows an explanation, so only a
              // practice test stores one.
              explanation: isPractice ? explanation.trim() || null : null,
              ...(kind === 'multiple_choice'
                ? { choices: validChoices, correctIndex }
                : { choices: [], correctIndex: null }),
            })
          }
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
